// === IMPORTS ===
import React, { useEffect } from 'react';
import { Trash2, X, AlertTriangle } from 'lucide-react';
import AppIcon from './AppIcon';

import '../styles/twin-modals-shared.css';
import './ConfirmDeleteModal.css';

export type DeleteItemType = 'room' | 'zone' | 'furniture' | 'agent' | 'location' | 'interaction' | 'tile' | 'generic';

export interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  itemName?: string;
  itemIcon?: string;
  itemType?: DeleteItemType;
  description?: string;
  warningText?: string;
  confirmText?: string;
  cancelText?: string;
}

// === COMPONENTE: MODAL DE CONFIRMACIÓN DE BORRADO ===
export const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  itemName,
  itemIcon,
  itemType = 'generic',
  description,
  warningText,
  confirmText = 'Delete',
  cancelText = 'Cancel',
}) => {
  // === CIERRE CON TECLA ESCAPE ===
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // === TEXTOS POR DEFECTO SEGÚN EL TIPO DE ELEMENTO ===
  const resolvedTitle =
    title ||
    (itemType === 'room' || itemType === 'zone'
      ? 'Delete this zone?'
      : itemType === 'furniture'
      ? 'Delete this resource?'
      : itemType === 'agent'
      ? 'Unplace this agent?'
      : itemType === 'location'
      ? 'Delete this floor/location?'
      : itemType === 'interaction'
      ? 'Delete this interaction?'
      : itemType === 'tile'
      ? 'Clear this tile?'
      : 'Confirm deletion?');

  const resolvedDescription =
    description ||
    (itemType === 'room' || itemType === 'zone'
      ? `Are you sure you want to delete the zone "${itemName || 'selected'}"? Its boundaries and space configurations will be removed.`
      : itemType === 'furniture'
      ? `Are you sure you want to delete "${itemName || 'this resource'}" from the layout?`
      : itemType === 'agent'
      ? `Do you want to unplace "${itemName || 'this agent'}" from the office layout?`
      : itemType === 'location'
      ? `Are you sure you want to delete the floor "${itemName || 'selected'}"? All contained items will be removed.`
      : itemType === 'interaction'
      ? `Are you sure you want to delete the interaction "${itemName || 'selected'}"?`
      : itemType === 'tile'
      ? 'Resources, agents, and custom flooring on this coordinate will be removed.'
      : `Are you sure you want to delete "${itemName || 'this item'}"? This action cannot be undone.`);

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  // === RENDER ===
  return (
    <div
      id="confirm-delete-modal-overlay"
      className="twin-modal-overlay"
      onClick={onClose}
    >
      <div
        id="confirm-delete-modal-container"
        className="twin-modal twin-modal--padded twin-confirm-delete-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* === BARRA DE ACENTO SUPERIOR === */}
        {/*<div className="twin-modal-accent-bar twin-confirm-delete-accent" />*/}

        {/* === BOTÓN CERRAR === */}
        <button
          id="confirm-delete-close-btn"
          type="button"
          onClick={onClose}
          className="twin-modal-close-btn"
          title="Cancel"
        >
          <X className="twin-icon-md" />
        </button>

        {/* === HEADER: ÍCONO + TÍTULO === */}
        <div className="twin-modal-header">
          <div className="twin-modal-icon-circle twin-confirm-delete-icon-circle">
            <Trash2 className="twin-icon-lg twin-icon-pulse" />
          </div>
          <div className="twin-confirm-delete-title-block">
            <h3 className="twin-modal-title">{resolvedTitle}</h3>
            {itemName && (
              <div className="twin-confirm-delete-item-row">
                {itemIcon && (
                  <AppIcon icon={itemIcon} name={itemName} className="twin-confirm-delete-item-icon" />
                )}
                <span className="twin-confirm-delete-item-name">{itemName}</span>
              </div>
            )}
          </div>
        </div>

        {/* === CUERPO: DESCRIPCIÓN Y ADVERTENCIA === */}
        <div className="twin-modal-description-box">
          <p className="twin-modal-description-text">{resolvedDescription}</p>

          {warningText && (
            <div className="twin-confirm-delete-warning">
              <AlertTriangle className="twin-icon-sm" />
              <span className="twin-confirm-delete-warning-text">{warningText}</span>
            </div>
          )}
        </div>

        {/* === FOOTER: BOTONES === */}
        <div className="twin-modal-footer">
          <button
            id="confirm-delete-cancel-btn"
            type="button"
            onClick={onClose}
            className="twin-btn twin-btn--secondary"
          >
            {cancelText}
          </button>
          <button
            id="confirm-delete-action-btn"
            type="button"
            onClick={handleConfirm}
            className="twin-btn twin-btn--danger"
          >
            <Trash2 className="twin-icon-md" />
            <span>{confirmText}</span>
          </button>
        </div>
      </div>
    </div>
  );
};